import { computed, signal, Signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DatapointAdapter, DatapointView, CompareEntry, pctDelta } from '../datapoint.model';

export interface MaturityAdapterDeps {
  measurementId: string;
  measurementName: string;
  time: (Date | string)[];
  series: { key: string; label: string; unit?: string; values: (number | null)[] }[];
  startIndex: number;
  highlightKey?: string;
}

export class MaturityDatapointAdapter implements DatapointAdapter {
  private readonly cursor = signal(this.deps.startIndex);
  readonly current: Signal<DatapointView> = computed(() => this.buildView(this.cursor()));
  readonly canDelete = computed(() => false); // Maturity removal not enabled yet (see idea note)

  constructor(private readonly deps: MaturityAdapterDeps) {}

  next(): void {
    this.cursor.update((i) => Math.min(i + 1, this.deps.time.length - 1));
  }
  prev(): void {
    this.cursor.update((i) => Math.max(i - 1, 0));
  }
  delete(): Observable<unknown> {
    return of(null); // not supported for Maturity yet
  }

  private compareAgainst(i: number, j: number): CompareEntry[] {
    return this.deps.series
      .map((s) => {
        const a = s.values[i];
        const b = s.values[j];
        if (typeof a !== 'number' || typeof b !== 'number') return null;
        return { key: s.key, label: s.label, delta: a - b, pct: pctDelta(b, a) };
      })
      .filter((x): x is CompareEntry => x !== null);
  }

  private buildView(i: number): DatapointView {
    const total = this.deps.time.length;
    const view: DatapointView = {
      sourceLabel: this.deps.measurementName,
      timestamp: new Date(this.deps.time[i]),
      values: this.deps.series.map((s) => ({
        key: s.key, label: s.label, unit: s.unit, value: s.values[i] ?? null,
      })),
      highlightKey: this.deps.highlightKey,
      nav: { index: i, total, hasPrev: i > 0, hasNext: i < total - 1 },
      compare: {},
      attachments: [],
    };
    if (i > 0) view.compare.prev = this.compareAgainst(i, i - 1);
    if (i < total - 1) view.compare.next = this.compareAgainst(i, i + 1);
    return view;
  }
}
